import type { BillOut } from "../../../../../api/bills";
import { depositPaymentMethodOf } from "../../../../../api/paymentMethods";
import { useIsEdgeLocal } from "../../../../../hooks/useEdgeMode";
import { usePaymentMethods } from "../../../../../hooks/usePaymentMethods";
import { formatCents, parseMoneyToCents } from "../../../../../utils/money";
import {
  attachedDepositOf,
  depositCoverageOf,
  type AttachedBillDeposit,
  type DepositCoverage,
} from "../deposits/billDeposit";

/** Каквото „Закрий" показва за депозита — закаченият или записаният. */
export type CloseDepositChoice = AttachedBillDeposit;

/**
 * Депозитът, записан при закриването на сметката — повторният опит („Закрий"
 * на закрита без бон, „Фискализирай" от „Възстановяване") го показва само за
 * четене: тялото на `/close/retry` е празно и BE ползва записаните метод и
 * депозит (cross-repo-contract ред 53).
 */
export interface RecordedBillDeposit {
  depositId: number;
  /** Платеното с депозита (`deposit_amount`) — низ или JSON число. */
  appliedAmount: string | number;
  /** Методът по сметката: на остатъка или „Плащане с депозит". */
  paymentMethodId: number | null;
}

/** Записаният депозит от сметката (`GET /bills` или историята); null без него. */
export const recordedBillDepositOf = (
  bill:
    | {
        deposit_id?: number | null;
        deposit_amount?: number | string;
        payment_method_id?: number | null;
      }
    | null
    | undefined,
): RecordedBillDeposit | null =>
  bill?.deposit_id
    ? {
        depositId: bill.deposit_id,
        appliedAmount: bill.deposit_amount ?? 0,
        paymentMethodId: bill.payment_method_id ?? null,
      }
    : null;

export interface CloseDeposit {
  /** Има ли депозит в „Закрий": закачен към сметката (първо закриване) или
   *  записан (повторен опит). Иначе секцията я няма и модалът е старият. */
  available: boolean;
  /** Повторен опит на сметка, платена с депозит: депозитът и методът са
   *  записаните — само за четене, тялото на повтора е празно. */
  recorded: boolean;
  /** При `recorded` — записаният метод на остатъка (null — депозитът покрива
   *  всичко); иначе null. */
  recordedMethodId: number | null;
  /** Депозитът, който ще се приспадне; null — без депозит или без интернет. */
  deposit: CloseDepositChoice | null;
  coverage: DepositCoverage | null;
  /** id-то на „Плащане с депозит" — методът, когато депозитът покрива всичко. */
  depositMethodId: number | null;
  /** Локален режим със закачен депозит: касата не приспада депозити — модалът
   *  казва, че сметката се плаща изцяло, а депозитът остава свободен (BE го
   *  освобождава при replay-а). */
  offlineDeposit: CloseDepositChoice | null;
  /** Сметка 0.00 със закачен депозит: BE отказва закриването — първо „Махни
   *  депозита" (или го закачете към масата, на която са редовете). */
  blocksZeroBill: boolean;
}

const NO_DEPOSIT: CloseDeposit = {
  available: false,
  recorded: false,
  recordedMethodId: null,
  deposit: null,
  coverage: null,
  depositMethodId: null,
  offlineDeposit: null,
  blocksZeroBill: false,
};

/**
 * Депозитът в „Закрий" (BE `deposits/service_bills.py`). Изборът му НЕ е тук —
 * той е „Депозит" → „Използвай депозит" (закача го към сметката), а „Закрий"
 * показва закачения само за четене. Ползва се ЦЕЛИЯТ: по-голям от сметката —
 * разликата изгаря; по-малък — остатъкът е с избрания метод (и „Смесено" —
 * разпределя само остатъка). Без бакшиш (BE го отказва). Покритието се
 * смята от СВЕЖАТА сума при „Закриване". Повторният опит на платена с депозит
 * сметка показва записания (`recorded`).
 */
export function useCloseDeposit({
  isRetry,
  totalAmount,
  bill,
  recordedDeposit = null,
}: {
  isRetry: boolean;
  totalAmount: string;
  /** Сметката от списъка — закаченият депозит е в документа ѝ. */
  bill: Pick<BillOut, "status" | "deposit_id" | "deposit_name" | "deposit_full_amount"> | null;
  /** Само при повторен опит — депозитът, записан при закриването. */
  recordedDeposit?: RecordedBillDeposit | null;
}): CloseDeposit {
  const edgeLocal = useIsEdgeLocal();
  const { data: allMethods = [] } = usePaymentMethods();
  const depositMethodId = depositPaymentMethodOf(allMethods)?.id ?? null;
  const billCents = parseMoneyToCents(totalAmount) ?? 0;

  // Повторен опит на сметка, платена с депозит: нищо не се избира — показва
  // се каквото е записано, а BE повтаря бона със записаните метод и депозит.
  if (isRetry) {
    if (!recordedDeposit) return NO_DEPOSIT;
    const appliedCents = parseMoneyToCents(recordedDeposit.appliedAmount) ?? 0;
    const coverage = depositCoverageOf(formatCents(appliedCents), billCents);
    return {
      ...NO_DEPOSIT,
      available: true,
      recorded: true,
      recordedMethodId:
        coverage.remainderCents > 0 && recordedDeposit.paymentMethodId !== depositMethodId
          ? recordedDeposit.paymentMethodId
          : null,
      deposit: {
        id: recordedDeposit.depositId,
        // Името не е в записа на историята — кутийката показва само номера.
        name: "",
        amount: formatCents(appliedCents),
      },
      coverage,
      depositMethodId,
    };
  }

  const attached = attachedDepositOf(bill);
  if (!attached) return NO_DEPOSIT;
  if (edgeLocal) return { ...NO_DEPOSIT, available: true, offlineDeposit: attached };
  return {
    ...NO_DEPOSIT,
    available: true,
    deposit: attached,
    coverage: depositCoverageOf(attached.amount, billCents),
    depositMethodId,
    blocksZeroBill: billCents <= 0,
  };
}
